import React from 'react';
import { motion } from 'framer-motion';
import { GitCompare, ArrowRight, Zap, Database, Terminal, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Compare: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-32 pb-20 px-8">
      <div className="max-w-7xl mx-auto">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-[#525252] hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Terminal
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <GitCompare className="w-8 h-8 text-emerald-500" />
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-white uppercase">Trace Comparison</h1>
          </div>
          <p className="text-[#525252] font-mono text-lg max-w-2xl leading-relaxed">
            Compare two transactions to identify differences in call trees, storage mutations, and gas consumption.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="p-8 rounded-3xl bg-[#0F0F0F] border border-[#262626] flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#525252]">Base Transaction</label>
                <input 
                  type="text" 
                  placeholder="0x..." 
                  className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-emerald-500/50"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#525252]">Comparison Transaction</label>
                <input 
                  type="text" 
                  placeholder="0x..." 
                  className="bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-emerald-500/50"
                />
              </div>
              <button className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 py-4 rounded-xl transition-all text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                Compare Traces
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {[
              { icon: <Zap className="w-5 h-5 text-yellow-500" />, title: "Diff Call Trees", desc: "Identify new or missing contract calls between transactions." },
              { icon: <Database className="w-5 h-5 text-blue-500" />, title: "Diff Storage Mutations", desc: "Compare state changes and identify diverging execution paths." },
              { icon: <Terminal className="w-5 h-5 text-purple-500" />, title: "Diff Gas Usage", desc: "Analyze gas consumption differences at the opcode level." }
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-[#0F0F0F] border border-[#262626] flex items-center gap-6 group hover:border-emerald-500/30 transition-all">
                <div className="p-3 bg-[#1A1A1A] rounded-xl group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-white uppercase tracking-tight mb-1">{item.title}</h4>
                  <p className="text-xs text-[#525252] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
